import { createOverlayCoordinator } from './overlay-coordinator.js';

export const DEFAULT_POPUP_POLICY = Object.freeze({
  teaserDelayMs: 30_000,
  teaserScrollThreshold: 0.5,
  desktopExitDelayMs: 5_000,
  suppressionMs: 30 * 24 * 60 * 60 * 1_000,
  maxEligibleSessions: 3,
});

export const advanceActiveTime = (activeMs, elapsedMs, isVisible) => {
  const current = Number.isFinite(activeMs) ? Math.max(0, activeMs) : 0;
  const elapsed = Number.isFinite(elapsedMs) ? Math.max(0, elapsedMs) : 0;
  return isVisible ? current + elapsed : current;
};

export const parseStoredTimestamp = (raw) => {
  if (raw === null || raw === undefined || raw === '') return null;

  let value = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      value = raw;
    }
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) && value >= 0 ? value : null;
  }

  if (typeof value !== 'string') return null;
  if (/^\d+$/.test(value)) {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }

  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
};

export const parseStoredExposureCount = (raw) => {
  if (typeof raw !== 'string' || !/^(0|[1-9]\d*)$/.test(raw)) return 0;
  const value = Number(raw);
  return Number.isSafeInteger(value) ? value : 0;
};

const isWithinSuppressionWindow = (timestamp, nowMs, suppressionMs) => {
  if (!Number.isFinite(timestamp) || !Number.isFinite(nowMs)) return false;
  return Math.max(0, nowMs - timestamp) < suppressionMs;
};

export const resolveOfferSessionCadence = (state, policy = DEFAULT_POPUP_POLICY) => {
  let exposureCount = Number.isInteger(state.exposureCount) && state.exposureCount >= 0
    ? state.exposureCount
    : 0;
  let cooldownAtMs = Number.isFinite(state.cooldownAtMs) && state.cooldownAtMs >= 0
    ? state.cooldownAtMs
    : null;
  let countedThisSession = state.countedThisSession === true;

  if (isWithinSuppressionWindow(cooldownAtMs, state.nowMs, policy.suppressionMs)) {
    return {
      exposureCount,
      cooldownAtMs,
      countedThisSession,
      offerSuppressed: true,
    };
  }

  if (cooldownAtMs !== null) {
    exposureCount = 0;
    cooldownAtMs = null;
    countedThisSession = false;
  }

  if (
    !state.eligible
    || isWithinSuppressionWindow(state.submittedAtMs, state.nowMs, policy.suppressionMs)
  ) {
    return {
      exposureCount,
      cooldownAtMs,
      countedThisSession,
      offerSuppressed: true,
    };
  }

  if (countedThisSession) {
    return {
      exposureCount,
      cooldownAtMs,
      countedThisSession,
      offerSuppressed: false,
    };
  }

  if (exposureCount >= policy.maxEligibleSessions) {
    return {
      exposureCount: 0,
      cooldownAtMs: state.nowMs,
      countedThisSession: false,
      offerSuppressed: true,
    };
  }

  return {
    exposureCount: exposureCount + 1,
    cooldownAtMs,
    countedThisSession: true,
    offerSuppressed: false,
  };
};

export const createPopupStorageAdapter = (storage) => {
  try {
    const probe = '__budziko_popup_probe__';
    storage.setItem(probe, probe);
    storage.removeItem(probe);
    const read = (key) => {
      try {
        return { ok: true, value: storage.getItem(key) };
      } catch {
        return { ok: false, value: null };
      }
    };
    return {
      available: true,
      read,
      get: (key) => read(key).value,
      set: (key, value) => {
        try { storage.setItem(key, value); return true; } catch { return false; }
      },
      remove: (key) => {
        try { storage.removeItem(key); return true; } catch { return false; }
      },
    };
  } catch {
    return {
      available: false,
      read: () => ({ ok: false, value: null }),
      get: () => null,
      set: () => false,
      remove: () => false,
    };
  }
};

const restoreStorageValue = ({ storage, key, previous }) => (
  previous === null ? storage.remove(key) : storage.set(key, previous)
);

const persistStorageChanges = (changes) => {
  const completed = [];
  for (const change of changes) {
    const succeeded = change.value === null
      ? change.storage.remove(change.key)
      : change.storage.set(change.key, change.value);
    if (!succeeded) {
      [change, ...completed.reverse()].forEach(restoreStorageValue);
      return false;
    }
    completed.push(change);
  }
  return true;
};

export const synchronizeOfferCadence = ({
  local,
  session,
  exposureCountKey,
  cooldownKey,
  exposureCountedSessionKey,
  eligible,
  submittedAtMs,
  nowMs,
}, policy = DEFAULT_POPUP_POLICY) => {
  if (!local.available || !session.available) return { ok: false };

  const exposureRead = local.read(exposureCountKey);
  const cooldownRead = local.read(cooldownKey);
  const countedRead = session.read(exposureCountedSessionKey);
  if (!exposureRead.ok || !cooldownRead.ok || !countedRead.ok) return { ok: false };

  const storedExposureCount = exposureRead.value;
  const storedCooldown = cooldownRead.value;
  const storedExposureCountedSession = countedRead.value;
  let exposureCount = parseStoredExposureCount(storedExposureCount);
  let cooldownAtMs = parseStoredTimestamp(storedCooldown);
  let exposureCountedThisSession = storedExposureCountedSession === '1';
  const cadence = resolveOfferSessionCadence({
    eligible,
    submittedAtMs,
    cooldownAtMs,
    exposureCount,
    countedThisSession: exposureCountedThisSession,
    nowMs,
  }, policy);
  exposureCount = cadence.exposureCount;
  cooldownAtMs = cadence.cooldownAtMs;
  exposureCountedThisSession = cadence.countedThisSession;

  const changes = [];
  if (String(exposureCount) !== storedExposureCount) {
    changes.push({
      storage: local,
      key: exposureCountKey,
      value: String(exposureCount),
      previous: storedExposureCount,
    });
  }
  if (cooldownAtMs === null) {
    if (storedCooldown !== null) {
      changes.push({ storage: local, key: cooldownKey, value: null, previous: storedCooldown });
    }
  } else if (String(cooldownAtMs) !== storedCooldown) {
    changes.push({
      storage: local,
      key: cooldownKey,
      value: String(cooldownAtMs),
      previous: storedCooldown,
    });
  }
  if (exposureCountedThisSession) {
    if (storedExposureCountedSession !== '1') {
      changes.push({
        storage: session,
        key: exposureCountedSessionKey,
        value: '1',
        previous: storedExposureCountedSession,
      });
    }
  } else if (storedExposureCountedSession !== null) {
    changes.push({
      storage: session,
      key: exposureCountedSessionKey,
      value: null,
      previous: storedExposureCountedSession,
    });
  }

  if (!persistStorageChanges(changes)) return { ok: false };
  return { ok: true, ...cadence };
};

export const resolveAutoShownThisSession = (persistenceUsable, storedValue) => (
  !persistenceUsable || storedValue === '1'
);

export const resetOfferPersistence = ({
  local,
  session,
  submitKey,
  dismissKey,
  dismissedSessionKey,
  autoShownSessionKey,
  exposureCountKey,
  cooldownKey,
  exposureCountedSessionKey,
  submittedAtMs,
}) => {
  const targets = [
    { storage: local, key: submitKey, value: String(submittedAtMs) },
    { storage: local, key: exposureCountKey, value: '0' },
    { storage: local, key: cooldownKey, value: null },
    { storage: local, key: dismissKey, value: null },
    { storage: session, key: exposureCountedSessionKey, value: null },
    { storage: session, key: dismissedSessionKey, value: null },
    { storage: session, key: autoShownSessionKey, value: null },
  ];
  const captured = targets.map((target) => ({ ...target, read: target.storage.read(target.key) }));
  if (captured.some(({ read }) => !read.ok)) return false;
  const changes = captured
    .filter(({ read, value }) => read.value !== value)
    .map(({ read, ...change }) => ({ ...change, previous: read.value }));
  return persistStorageChanges(changes);
};

export const decidePopupPresentation = (state, policy = DEFAULT_POPUP_POLICY) => {
  if (state.blockedByAgeGate) return 'hidden';
  if (state.blockedByChat) return 'hidden';
  if (state.postedSuccess || state.postedError) return 'form';
  if (!state.eligible) return 'hidden';
  if (
    isWithinSuppressionWindow(state.submittedAtMs, state.nowMs, policy.suppressionMs)
    || isWithinSuppressionWindow(state.offerCooldownAtMs, state.nowMs, policy.suppressionMs)
  ) {
    return 'hidden';
  }
  if (state.explicitOpen) return 'form';
  if (state.formVisible) return 'form';
  if (state.dismissedThisSession) return 'hidden';
  if (state.autoShownThisSession) return 'teaser';
  if (
    isWithinSuppressionWindow(state.autoOpenSuppressedAtMs, state.nowMs, policy.suppressionMs)
  ) {
    return 'teaser';
  }
  if (
    state.device === 'desktop'
    && state.exitIntent
    && state.activeMs >= policy.desktopExitDelayMs
  ) {
    return 'form';
  }
  if (
    state.activeMs >= policy.teaserDelayMs
    || state.scrollRatio >= policy.teaserScrollThreshold
  ) {
    return 'teaser';
  }
  return 'hidden';
};

export const applyPopupDomPresentation = ({
  popup,
  teaser,
  presentation,
  focusTarget = null,
  requestFrame = (callback) => window.requestAnimationFrame(callback),
}) => {
  const formVisible = presentation === 'form';

  if (formVisible) popup.classList.add('visible', 'animation--revealed');
  else popup.classList.remove('visible', 'animation--revealed');

  popup.setAttribute('aria-hidden', formVisible ? 'false' : 'true');
  teaser.hidden = presentation !== 'teaser';
  teaser.setAttribute('aria-expanded', formVisible ? 'true' : 'false');

  if (formVisible && focusTarget) requestFrame(() => focusTarget.focus());
  return formVisible;
};

export const bindPopupTeaserActivation = ({ teaser, activate }) => {
  const onTeaserClick = (event) => activate(event);
  teaser.addEventListener('click', onTeaserClick);
  return () => teaser.removeEventListener('click', onTeaserClick);
};

export const createPopupController = ({
  popup,
  document: doc,
  window: win,
  performance: clock,
  MutationObserver: MutationObserverClass,
  local,
  session,
  getEligiblePageViews,
  scrollRatio,
  ageGateVisible,
}) => {
  if (popup.dataset.popupBehaviorReady === 'true') return null;
  popup.dataset.popupBehaviorReady = 'true';

  const teaser = popup.nextElementSibling?.matches('[data-popup-teaser]')
    ? popup.nextElementSibling
    : null;
  const close = popup.querySelector('[data-close]');
  const email = popup.querySelector('input[type="email"]');
  const errorStatus = popup.querySelector('[data-popup-error] [data-form-status]');
  if (!teaser || !close) return null;

  const id = popup.dataset.id || popup.id || 'offer';
  const dismissKey = popup.dataset.dismissKey || `fluco_popup-${id}`;
  const submitKey = popup.dataset.submitKey || `budziko_popup_submitted_${id}`;
  const dismissedSessionKey = `budziko_popup_dismissed_${id}`;
  const autoShownSessionKey = `budziko_popup_auto_shown_${id}`;
  const exposureCountKey = `budziko_popup_exposure_count_${id}`;
  const cooldownKey = `budziko_popup_cooldown_${id}`;
  const exposureCountedSessionKey = `budziko_popup_exposure_counted_${id}`;
  const device = win.matchMedia('(hover: hover) and (pointer: fine)').matches
    ? 'desktop'
    : 'mobile';
  const designMode = Boolean(win.Shopify?.designMode);
  const canRememberExposure = local.available && session.available;
  const eligible = popup.dataset.offerEligible !== 'false';
  const isActiveViewing = () => !doc.hidden && !ageGateVisible();

  let activeMs = 0;
  let lastTickMs = clock.now();
  let wasActive = isActiveViewing();
  let maximumScrollRatio = scrollRatio();
  const dismissalRead = local.read(dismissKey);
  const submissionRead = local.read(submitKey);
  const dismissedSessionRead = session.read(dismissedSessionKey);
  const autoShownSessionRead = session.read(autoShownSessionKey);
  let persistenceUsable = canRememberExposure
    && dismissalRead.ok
    && submissionRead.ok
    && dismissedSessionRead.ok
    && autoShownSessionRead.ok;
  let autoOpenSuppressedAtMs = parseStoredTimestamp(dismissalRead.value);
  let submittedAtMs = parseStoredTimestamp(submissionRead.value);
  let exposureCount = 0;
  let cooldownAtMs = null;
  let exposureCountedThisSession = false;
  let dismissedThisSession = dismissedSessionRead.value === '1';
  let formVisible = false;
  let postedSuccess = Boolean(popup.querySelector('[data-popup-posted]'));
  let postedError = Boolean(popup.querySelector('[data-popup-error]'));
  let explicitOpen = false;
  let openedExplicitly = false;

  if (persistenceUsable) {
    const cadencePersistence = synchronizeOfferCadence({
      local,
      session,
      exposureCountKey,
      cooldownKey,
      exposureCountedSessionKey,
      eligible,
      submittedAtMs,
      nowMs: Date.now(),
    }, DEFAULT_POPUP_POLICY);
    if (cadencePersistence.ok) {
      exposureCount = cadencePersistence.exposureCount;
      cooldownAtMs = cadencePersistence.cooldownAtMs;
      exposureCountedThisSession = cadencePersistence.countedThisSession;
    } else {
      persistenceUsable = false;
    }
  }
  let autoShownThisSession = resolveAutoShownThisSession(
    persistenceUsable,
    autoShownSessionRead.value,
  );

  const failClosed = () => {
    persistenceUsable = false;
    autoShownThisSession = true;
  };

  const rememberedEligiblePageViews = () => {
    if (!persistenceUsable) return 1;
    const result = getEligiblePageViews();
    if (!result.ok) failClosed();
    return result.value;
  };

  let evaluateCoordinatorChange = () => {};
  const overlayCoordinator = createOverlayCoordinator({
    document: doc,
    onChange: () => evaluateCoordinatorChange(),
  });

  const state = (overrides = {}) => ({
    eligible,
    submittedAtMs,
    autoOpenSuppressedAtMs,
    offerCooldownAtMs: cooldownAtMs,
    dismissedThisSession,
    blockedByAgeGate: ageGateVisible(),
    blockedByChat: overlayCoordinator.suppressesWelcome(),
    nowMs: Date.now(),
    device,
    activeMs,
    scrollRatio: maximumScrollRatio,
    pageViews: eligible ? rememberedEligiblePageViews() : 0,
    exitIntent: false,
    explicitOpen,
    autoShownThisSession,
    formVisible,
    postedSuccess,
    postedError,
    ...overrides,
  });

  const setHidden = () => {
    formVisible = applyPopupDomPresentation({ popup, teaser, presentation: 'hidden' });
  };

  const showTeaser = () => {
    formVisible = applyPopupDomPresentation({ popup, teaser, presentation: 'teaser' });
  };

  const showForm = ({ focusEmail = false, focusError = false, automatic = false } = {}) => {
    if (automatic && !designMode) {
      autoShownThisSession = true;
      if (!persistenceUsable || !session.set(autoShownSessionKey, '1')) {
        failClosed();
        showTeaser();
        return;
      }
    }

    if (focusEmail && email) openedExplicitly = true;
    const focusTarget = focusEmail && email ? email : focusError ? errorStatus : null;
    formVisible = applyPopupDomPresentation({
      popup,
      teaser,
      presentation: 'form',
      focusTarget,
      requestFrame: (callback) => win.requestAnimationFrame(callback),
    });
  };

  const applyPresentation = (presentation, options = {}) => {
    const coordinatorState = overlayCoordinator.setWelcomeVisible(
      presentation === 'teaser' || presentation === 'form',
    );
    const synchronizedPresentation = coordinatorState.suppressWelcome
      ? 'hidden'
      : presentation;

    if (synchronizedPresentation === 'form') showForm(options);
    else if (synchronizedPresentation === 'teaser') showTeaser();
    else setHidden();
  };

  let evaluating = false;
  const evaluate = (overrides = {}) => {
    if (evaluating) return;
    evaluating = true;
    try {
      const presentation = decidePopupPresentation(state(overrides));
      const automatic = presentation === 'form'
        && !explicitOpen
        && !postedSuccess
        && !postedError
        && !formVisible;
      const focusError = presentation === 'form' && postedError && !formVisible;
      applyPresentation(presentation, { automatic, focusError });
      explicitOpen = false;
    } finally {
      evaluating = false;
    }
  };
  evaluateCoordinatorChange = evaluate;

  const updateActiveClock = () => {
    const nowMs = clock.now();
    activeMs = advanceActiveTime(activeMs, nowMs - lastTickMs, wasActive);
    lastTickMs = nowMs;
    wasActive = isActiveViewing();
  };

  const tick = () => {
    updateActiveClock();
    evaluate();
  };

  const dismiss = ({ persist = true } = {}) => {
    const shouldRestoreFocus = openedExplicitly;
    openedExplicitly = false;
    explicitOpen = false;
    formVisible = false;
    postedSuccess = false;
    postedError = false;

    if (persist && !designMode) {
      autoOpenSuppressedAtMs = Date.now();
      dismissedThisSession = true;
      const dismissalPersisted = persistenceUsable && local.set(
        dismissKey,
        JSON.stringify(new Date(autoOpenSuppressedAtMs).toISOString()),
      );
      const sessionDismissalPersisted = persistenceUsable
        && session.set(dismissedSessionKey, '1');
      if (!dismissalPersisted || !sessionDismissalPersisted) failClosed();
    }

    applyPresentation(persist ? decidePopupPresentation(state()) : 'hidden');
    if (shouldRestoreFocus && !teaser.hidden) teaser.focus();
  };

  const onTeaserClick = () => {
    updateActiveClock();
    explicitOpen = true;
    applyPresentation(decidePopupPresentation(state()), { focusEmail: true });
    explicitOpen = false;
  };
  const onCloseClick = (event) => {
    event.preventDefault();
    dismiss();
  };
  const onKeydown = (event) => {
    if (event.key === 'Escape' && formVisible) dismiss();
  };
  const onScroll = () => {
    maximumScrollRatio = Math.max(maximumScrollRatio, scrollRatio());
  };
  const onExitIntent = (event) => {
    if (device !== 'desktop' || event.relatedTarget || event.clientY > 0) return;
    updateActiveClock();
    evaluate({ exitIntent: true });
  };

  const ageGateObserver = new MutationObserverClass((mutations) => {
    if (!mutations.some(({ target }) => target.matches?.('[data-popup-type="age_popup"]'))) {
      return;
    }
    updateActiveClock();
    evaluate();
  });
  doc.querySelectorAll('[data-popup-type="age_popup"]').forEach((ageGate) => {
    ageGateObserver.observe(ageGate, { attributes: true, attributeFilter: ['class'] });
  });

  const unbindTeaserActivation = bindPopupTeaserActivation({
    teaser,
    activate: onTeaserClick,
  });
  close.addEventListener('click', onCloseClick);
  doc.addEventListener('keydown', onKeydown);
  doc.addEventListener('visibilitychange', tick);
  win.addEventListener('scroll', onScroll, { passive: true });
  doc.addEventListener('mouseout', onExitIntent);

  let timer = null;
  if (!designMode) timer = win.setInterval(tick, 250);

  if (postedSuccess) {
    submittedAtMs = Date.now();
    exposureCount = 0;
    cooldownAtMs = null;
    exposureCountedThisSession = false;
    autoOpenSuppressedAtMs = null;
    dismissedThisSession = false;
    autoShownThisSession = false;
    if (!resetOfferPersistence({
      local,
      session,
      submitKey,
      dismissKey,
      dismissedSessionKey,
      autoShownSessionKey,
      exposureCountKey,
      cooldownKey,
      exposureCountedSessionKey,
      submittedAtMs,
    })) {
      failClosed();
    }
    popup.classList.add('popup--posted');
  }
  evaluate();

  return {
    select: () => applyPresentation(
      decidePopupPresentation(state({ explicitOpen: true })),
    ),
    deselect: () => dismiss({ persist: false }),
    destroy: () => {
      if (timer !== null) win.clearInterval(timer);
      ageGateObserver.disconnect();
      unbindTeaserActivation();
      close.removeEventListener('click', onCloseClick);
      doc.removeEventListener('keydown', onKeydown);
      doc.removeEventListener('visibilitychange', tick);
      win.removeEventListener('scroll', onScroll);
      doc.removeEventListener('mouseout', onExitIntent);
      overlayCoordinator.destroy();
    },
  };
};

export const initializeBrowserBehavior = ({
  documentObject = globalThis.document,
  windowObject = globalThis.window,
  performanceObject = globalThis.performance,
  MutationObserverClass = globalThis.MutationObserver,
} = {}) => {
  const doc = documentObject;
  const win = windowObject;
  const clock = performanceObject;
  const controllers = new WeakMap();
  let eligiblePageViews;

  const createStorage = (storageName) => {
    try {
      return createPopupStorageAdapter(win[storageName]);
    } catch {
      return createPopupStorageAdapter(null);
    }
  };

  const local = createStorage('localStorage');
  const session = createStorage('sessionStorage');

  const getEligiblePageViews = () => {
    if (eligiblePageViews !== undefined) return { ok: true, value: eligiblePageViews };
    if (!session.available) {
      return { ok: false, value: 1 };
    }

    const key = 'budziko_popup_eligible_page_views';
    const read = session.read(key);
    if (!read.ok) return { ok: false, value: 1 };
    const previous = Number.parseInt(read.value || '0', 10);
    eligiblePageViews = Number.isFinite(previous) ? Math.min(previous + 1, 99) : 1;
    if (!session.set(key, String(eligiblePageViews))) {
      eligiblePageViews = undefined;
      return { ok: false, value: 1 };
    }
    return { ok: true, value: eligiblePageViews };
  };

  const scrollRatio = () => {
    const scrollable = Math.max(0, doc.documentElement.scrollHeight - win.innerHeight);
    if (scrollable === 0) return 0;
    return Math.min(1, Math.max(0, win.scrollY / scrollable));
  };

  const ageGateVisible = () => Boolean(
    doc.querySelector('[data-popup-type="age_popup"].visible'),
  );

  const initializeOffer = (popup) => {
    const controller = createPopupController({
      popup,
      document: doc,
      window: win,
      performance: clock,
      MutationObserver: MutationObserverClass,
      local,
      session,
      getEligiblePageViews,
      scrollRatio,
      ageGateVisible,
    });
    if (controller) controllers.set(popup, controller);
  };

  const initializeOffers = (root = doc) => {
    const offers = [];
    if (root.matches?.('[data-offer-popup]')) offers.push(root);
    root.querySelectorAll?.('[data-offer-popup]').forEach((popup) => offers.push(popup));
    offers.forEach(initializeOffer);
  };

  const offerFromEvent = (event) => event.target.matches?.('[data-offer-popup]')
    ? event.target
    : event.target.querySelector?.('[data-offer-popup]');

  doc.addEventListener('shopify:section:load', (event) => initializeOffers(event.target));
  doc.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-offer-popup]').forEach((popup) => {
      controllers.get(popup)?.destroy();
      controllers.delete(popup);
    });
  });
  doc.addEventListener('shopify:block:select', (event) => {
    const popup = offerFromEvent(event);
    if (!popup) return;
    event.stopImmediatePropagation();
    controllers.get(popup)?.select();
  }, true);
  doc.addEventListener('shopify:block:deselect', (event) => {
    const popup = offerFromEvent(event);
    if (!popup) return;
    event.stopImmediatePropagation();
    controllers.get(popup)?.deselect();
  }, true);

  initializeOffers();
};

if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeBrowserBehavior, { once: true });
  } else {
    initializeBrowserBehavior();
  }
}
